import { Head, Link, useForm, router, usePage } from '@inertiajs/react'
import AppLayout from '../../layouts/AppLayout'
import { ArrowLeft, Mail, FileSignature, CheckCircle, XCircle, RefreshCw, Trash2, Eye, EyeOff, AlertTriangle, Send, Link2 as LinkIcon } from 'lucide-react'
import { useState } from 'react'

interface IntegrationField {
  name: string
  label: string
  type: string
  required?: boolean
  placeholder?: string
  value?: string | null
  help?: string
}

interface LastTestEmail {
  status: string | null
  message: string | null
  recipient: string | null
  sent_at: string | null
}

interface LastConnectionTest {
  at: string | null
  status: string | null
}

interface SenderVerification {
  status: 'verified' | 'unverified' | 'unknown' | 'unchecked'
  message: string | null
  sender_email: string | null
  checked_at: string | null
}
interface EmailReadiness {
  ready: boolean
  blocking_reason: string | null
  api_key: boolean
  api_key_source: string | null
  api_key_source_label: string | null
  api_key_decryptable: boolean
  decryption_error: string | null
  enabled: boolean
  sender: boolean
  sender_display: string | null
  sender_source: string | null
  sender_source_label: string | null
  sender_verification: SenderVerification | null
  app_host: boolean
  app_host_url: string | null
  app_host_source: string | null
  app_host_source_label: string | null
  app_host_warning: string | null
  last_connection_test: LastConnectionTest | null
  last_test_email: LastTestEmail | null
}

interface Integration {
  provider: string
  display_name: string
  description: string
  configured: boolean
  ready: boolean | null
  is_active: boolean
  masked_api_key: string | null
  masked_api_secret: string | null
  last_tested_at: string | null
  last_test_status: string | null
  sender_email: string | null
  sender_name: string | null
  readiness: EmailReadiness | null
  fields: IntegrationField[]
}

interface AppHostInfo {
  url: string | null
  configured: boolean
  source: string | null
  source_label: string | null
  warning: string | null
  saved_url: string | null
}

interface Props {
  integrations: Integration[]
  app_host: AppHostInfo
}

const providerIcons: Record<string, React.ComponentType<any>> = {
  sendgrid: Mail,
  docuseal: FileSignature
}

const providerColors: Record<string, string> = {
  sendgrid: 'bg-blue-100 text-blue-600',
  docuseal: 'bg-purple-100 text-purple-600'
}

export default function Integrations({ integrations, app_host }: Props) {
  return (
    <AppLayout>
      <Head title="Integrations" />

      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/settings" className="p-2 hover:bg-chestnut-cream rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5 text-chestnut-charcoal" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-display font-bold text-chestnut-charcoal">Integrations</h1>
            <p className="text-chestnut-charcoal/60 mt-1">Configure email and e-signature providers</p>
          </div>
          <Link href="/admin/email_tests" className="btn-secondary flex items-center gap-2 whitespace-nowrap">
            <Send className="h-4 w-4" />
            Test system emails
          </Link>
        </div>

        {app_host.warning && (
          <div className="flex items-start gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <span className="text-sm">{app_host.warning}</span>
          </div>
        )}

        <ApplicationUrlCard appHost={app_host} />

        <div className="grid gap-6">
          {integrations.map((integration) => (
            <IntegrationCard key={integration.provider} integration={integration} />
          ))}
        </div>
      </div>
    </AppLayout>
  )
}

// The public base URL every emailed link is built from. Editable here so an
// admin can point links at the real domain without an environment variable or
// a redeploy; clearing it hands resolution back to the environment.
function ApplicationUrlCard({ appHost }: { appHost: AppHostInfo }) {
  const { data, setData, patch, processing, errors } = useForm({
    application_url: appHost.saved_url || ''
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    patch('/integrations/app_url', { preserveScroll: true })
  }

  const clear = () => {
    router.patch(
      '/integrations/app_url',
      { application_url: '' },
      {
        preserveScroll: true,
        onSuccess: () => setData('application_url', '')
      }
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5 p-6">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-lg bg-chestnut-cream text-chestnut-forest">
          <LinkIcon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-chestnut-charcoal">Application URL</h3>
          <p className="text-sm text-chestnut-charcoal/60 mt-1">
            The public address links in emails point at — password resets, welcome emails and notification
            templates all use it.
          </p>

          <div className="mt-3 text-sm">
            {appHost.url ? (
              <p className="text-chestnut-charcoal">
                Email links currently use <span className="font-medium">{appHost.url}</span>
                {appHost.source_label && (
                  <span className="text-chestnut-charcoal/60"> — from {appHost.source_label}.</span>
                )}
              </p>
            ) : (
              <p className="text-amber-700">
                No application URL is set anywhere, so links in emails will be broken.
              </p>
            )}
          </div>

          <form onSubmit={submit} className="mt-4">
            <label htmlFor="application_url" className="block text-sm font-medium text-chestnut-charcoal mb-1">
              Saved application URL
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                id="application_url"
                type="text"
                value={data.application_url}
                onChange={(e) => setData('application_url', e.target.value)}
                placeholder="https://agent.example.com"
                className="flex-1 px-3 py-2 border border-chestnut-charcoal/20 rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 focus:border-chestnut-forest"
              />
              <button
                type="submit"
                disabled={processing}
                className="px-4 py-2 text-sm bg-chestnut-forest text-white rounded-lg hover:bg-chestnut-forest/90 transition-colors disabled:opacity-50"
              >
                {processing ? 'Saving...' : 'Save'}
              </button>
              {appHost.saved_url && (
                <button
                  type="button"
                  onClick={clear}
                  disabled={processing}
                  className="px-4 py-2 text-sm border border-chestnut-charcoal/20 rounded-lg hover:bg-chestnut-cream transition-colors disabled:opacity-50"
                >
                  Clear
                </button>
              )}
            </div>
            {errors.application_url && (
              <p className="mt-2 text-sm text-red-600">{errors.application_url}</p>
            )}
            <p className="mt-2 text-xs text-chestnut-charcoal/60">
              {appHost.saved_url
                ? 'Clearing this falls back to the APP_HOST environment variable, then the deployment domain.'
                : 'Leave blank to keep using the APP_HOST environment variable or the deployment domain.'}
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}

function formatDateTime(value: string | null) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

type ReadinessState = 'ok' | 'warn' | 'fail'
function ReadinessRow({
  ok,
  state,
  label,
  detail
}: {
  ok: boolean
  state?: ReadinessState
  label: string
  detail?: string | null
}) {
  const resolved: ReadinessState = state || (ok ? 'ok' : 'fail')

  return (
    <div className="flex items-start gap-2 text-sm">
      {resolved === 'ok' ? (
        <CheckCircle className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
      ) : resolved === 'warn' ? (
        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
      ) : (
        <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
      )}
      <span className="text-chestnut-charcoal">
        {label}
        {detail && <span className="text-chestnut-charcoal/60"> — {detail}</span>}
      </span>
    </div>
  )
}

function IntegrationCard({ integration }: { integration: Integration }) {
  const [isEditing, setIsEditing] = useState(false)
  const [showSecrets, setShowSecrets] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [testEmailTo, setTestEmailTo] = useState('')
  const [sendingTestEmail, setSendingTestEmail] = useState(false)
  const [testEmailResult, setTestEmailResult] = useState<{ success: boolean; message: string } | null>(null)

  const Icon = providerIcons[integration.provider] || Mail
  const colorClass = providerColors[integration.provider] || 'bg-gray-100 text-gray-600'

  const { csrf_token: csrfFromProps } = usePage<{ csrf_token?: string }>().props
  const csrfToken = () =>
    csrfFromProps ||
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ||
    ''

  const initialFormData = () => {
    const values: Record<string, string> = { provider: integration.provider, api_key: '', api_secret: '' }
    integration.fields.forEach((field) => {
      if (field.name === 'api_key' || field.name === 'api_secret') return
      values[field.name] = field.value || ''
    })
    return values
  }

  const { data, setData, post, processing, transform } = useForm<Record<string, string>>(initialFormData())

  // Inertia posts JSON, so `metadata[key]` field names have to be nested by
  // hand before they reach Rails.
  transform((values) => {
    const payload: Record<string, unknown> = {}
    const metadata: Record<string, string> = {}
    Object.entries(values).forEach(([key, value]) => {
      const match = key.match(/^metadata\[(.+)\]$/)
      if (match) {
        metadata[match[1]] = value
      } else {
        payload[key] = value
      }
    })
    if (Object.keys(metadata).length > 0) payload.metadata = metadata
    return payload
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/integrations/${integration.provider}`, {
      onSuccess: () => {
        setIsEditing(false)
        setData((previous) => ({ ...previous, api_key: '', api_secret: '' }))
      }
    })
  }

  const postJson = async (url: string, body?: Record<string, unknown>) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined
    })
    const result = await response.json().catch(() => null)
    return { response, result }
  }

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const { response, result } = await postJson(`/integrations/${integration.provider}/test`)
      if (result && typeof result.success === 'boolean') {
        setTestResult({
          success: result.success,
          message: result.message || (result.success ? 'Connection successful' : 'Connection failed')
        })
      } else {
        setTestResult({
          success: false,
          message: `Test request failed (HTTP ${response.status} ${response.statusText})`.trim()
        })
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? `Test failed: ${error.message}` : 'Test failed: network error'
      })
    } finally {
      setTesting(false)
    }
  }

  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    setSendingTestEmail(true)
    setTestEmailResult(null)
    try {
      const { response, result } = await postJson(`/integrations/${integration.provider}/test_email`, {
        to: testEmailTo
      })
      if (result && typeof result.success === 'boolean') {
        setTestEmailResult({
          success: result.success,
          message: result.message || (result.success ? 'Test email sent' : 'Test email failed')
        })
        if (result.success) router.reload({ only: ['integrations'] })
      } else {
        setTestEmailResult({
          success: false,
          message: `Test email request failed (HTTP ${response.status} ${response.statusText})`.trim()
        })
      }
    } catch (error) {
      setTestEmailResult({
        success: false,
        message: error instanceof Error ? `Test email failed: ${error.message}` : 'Test email failed: network error'
      })
    } finally {
      setSendingTestEmail(false)
    }
  }

  const handleDisconnect = () => {
    if (confirm(`Are you sure you want to disconnect ${integration.display_name}?`)) {
      router.delete(`/integrations/${integration.provider}`)
    }
  }

  const readiness = integration.readiness
  const lastTestEmail = readiness?.last_test_email || null
  const lastConnectionTest = readiness?.last_connection_test || null
  const emailReady = !!readiness && readiness.ready
  const senderVerification = readiness?.sender_verification || null
  // A sender SendGrid has rejected is a hard failure; a lookup that could not
  // run is only a warning — it is a missing answer, not a bad one.
  const senderVerificationState: ReadinessState =
    senderVerification?.status === 'verified' ? 'ok' : senderVerification?.status === 'unverified' ? 'fail' : 'warn'
  // Without a saved sender the blocking reason already names the missing piece.
  const blockingReason =
    readiness?.blocking_reason ||
    (senderVerification?.status === 'unverified' ? senderVerification.message : null)
  // "Connected" must mean email would really send, not just that a key exists.
  const connectedBadge = readiness ? readiness.ready : integration.configured

  return (
    <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${colorClass}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-chestnut-charcoal">{integration.display_name}</h3>
                {connectedBadge ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <CheckCircle className="h-3 w-3" />
                    Connected
                  </span>
                ) : integration.configured ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    <AlertTriangle className="h-3 w-3" />
                    Not ready
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    Not configured
                  </span>
                )}
              </div>
              <p className="text-sm text-chestnut-charcoal/60 mt-1">{integration.description}</p>

              {integration.configured && (
                <div className="mt-3 flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowSecrets(!showSecrets)}
                      className="text-chestnut-charcoal/60 hover:text-chestnut-charcoal"
                    >
                      {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <span className="text-chestnut-charcoal/60">
                      API Key: {showSecrets ? integration.masked_api_key : '••••••••'}
                    </span>
                  </div>
                  {integration.last_tested_at && (
                    <span className={`flex items-center gap-1 ${integration.last_test_status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {integration.last_test_status === 'success' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                      Last tested: {new Date(integration.last_tested_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {integration.configured && (
              <>
                <button
                  onClick={handleTest}
                  disabled={testing}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-chestnut-charcoal/20 rounded-lg hover:bg-chestnut-cream transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${testing ? 'animate-spin' : ''}`} />
                  Test
                </button>
                <button
                  onClick={handleDisconnect}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Disconnect
                </button>
              </>
            )}
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="px-4 py-1.5 text-sm bg-chestnut-forest text-white rounded-lg hover:bg-chestnut-forest/90 transition-colors"
            >
              {integration.configured ? 'Update' : 'Configure'}
            </button>
          </div>
        </div>

        {testResult && (
          <div className={`mt-4 p-3 rounded-lg ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            <div className="flex items-center gap-2">
              {testResult.success ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
              <span>{testResult.message}</span>
            </div>
          </div>
        )}

        {readiness && (
          <div className="mt-6 pt-6 border-t border-chestnut-charcoal/10">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-chestnut-charcoal">Email readiness</h4>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${emailReady ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
              >
                {emailReady ? 'Ready to send' : 'Needs attention'}
              </span>
            </div>

            {!readiness.ready && blockingReason && (
              <p className="mt-2 text-sm text-amber-700">{blockingReason}</p>
            )}

            <div className="mt-3 grid gap-2">
              <ReadinessRow
                ok={readiness.api_key}
                label="API key"
                detail={readiness.api_key ? readiness.api_key_source_label : 'not saved here and SENDGRID_API_KEY is not set'}
              />
              {!readiness.api_key_decryptable && (
                <ReadinessRow
                  ok={false}
                  label="Stored API key readable"
                  detail="could not be decrypted — check the ACTIVE_RECORD_ENCRYPTION_* variables on this deployment"
                />
              )}
              <ReadinessRow
                ok={readiness.enabled}
                label="Integration switched on"
                detail={readiness.enabled ? 'sending is enabled' : 'turned off — no email will be sent'}
              />
              <ReadinessRow
                ok={readiness.sender}
                label="Sender identity"
                detail={
                  readiness.sender
                    ? `${readiness.sender_display} — ${readiness.sender_source_label}`
                    : 'add a sender email below'
                }
              />
              <ReadinessRow
                ok={senderVerification?.status === 'verified'}
                state={senderVerificationState}
                label="Sender approved by SendGrid"
                detail={senderVerification?.message}
              />
              <ReadinessRow
                ok={readiness.app_host}
                label="Public host for email links"
                detail={
                  readiness.app_host_url
                    ? `${readiness.app_host_url} (from ${readiness.app_host_source_label || readiness.app_host_source})`
                    : 'save the Application URL above'
                }
              />
              <ReadinessRow
                ok={lastConnectionTest?.status === 'success'}
                label="Last connection test"
                detail={
                  lastConnectionTest?.at
                    ? `${lastConnectionTest.status === 'success' ? 'Key authenticated' : 'Failed'} on ${formatDateTime(lastConnectionTest.at)}`
                    : 'never tested — press Test above'
                }
              />
              <ReadinessRow
                ok={lastTestEmail?.status === 'success'}
                label="Last test email"
                detail={
                  lastTestEmail
                    ? `${lastTestEmail.status === 'success' ? 'Sent' : 'Failed'} to ${lastTestEmail.recipient} on ${formatDateTime(lastTestEmail.sent_at)}`
                    : 'never sent'
                }
              />
            </div>

            {readiness.decryption_error && (
              <p className="mt-2 text-xs text-red-600 break-words">{readiness.decryption_error}</p>
            )}

            {lastTestEmail?.message && lastTestEmail.status !== 'success' && (
              <p className="mt-2 text-xs text-red-600 break-words">{lastTestEmail.message}</p>
            )}

            {readiness.app_host_warning && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{readiness.app_host_warning}</span>
              </div>
            )}

            {integration.configured && (
              <form onSubmit={handleSendTestEmail} className="mt-4">
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Send test email</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    value={testEmailTo}
                    onChange={(e) => setTestEmailTo(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="flex-1 px-3 py-2 border border-chestnut-charcoal/20 rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 focus:border-chestnut-forest"
                  />
                  <button
                    type="submit"
                    disabled={sendingTestEmail}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm bg-chestnut-forest text-white rounded-lg hover:bg-chestnut-forest/90 transition-colors disabled:opacity-50"
                  >
                    <Send className={`h-4 w-4 ${sendingTestEmail ? 'animate-pulse' : ''}`} />
                    {sendingTestEmail ? 'Sending...' : 'Send test email'}
                  </button>
                </div>
                {testEmailResult && (
                  <div
                    className={`mt-3 p-3 rounded-lg text-sm break-words ${testEmailResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
                  >
                    <div className="flex items-start gap-2">
                      {testEmailResult.success ? (
                        <CheckCircle className="h-5 w-5 shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 shrink-0" />
                      )}
                      <span>{testEmailResult.message}</span>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>
        )}

        {isEditing && (
          <form onSubmit={handleSubmit} className="mt-6 pt-6 border-t border-chestnut-charcoal/10">
            <div className="grid gap-4">
              {integration.fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type={field.type}
                    value={data[field.name] || ''}
                    onChange={(e) => setData(field.name, e.target.value)}
                    placeholder={field.placeholder || (integration.configured ? '(leave blank to keep current)' : '')}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 focus:border-chestnut-forest"
                    required={field.required && !integration.configured}
                  />
                  {field.help && <p className="mt-1 text-xs text-chestnut-charcoal/60">{field.help}</p>}
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 text-sm border border-chestnut-charcoal/20 rounded-lg hover:bg-chestnut-cream transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing}
                className="px-4 py-2 text-sm bg-chestnut-forest text-white rounded-lg hover:bg-chestnut-forest/90 transition-colors disabled:opacity-50"
              >
                {processing ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
